const page = require('page');
const journeySchema = require('data/journeys');
const model = require('model/model');
const pubsub = require('events/pubsub');
const accountService = require('services/accountService');
const userContext = require('core/userContext');
const PromiseQueue = require('async/PromiseQueue');
const queue = new PromiseQueue();

const DOMWatcher = require('core/domWatcher');
const { isVisible } = require('utilities/html');
const logger = require('utilities/logger');

const watchers = new Map();

/**
 * @typedef {Object} MILESTONES
 * Data values:
 * * n: count of update calls for each milestone
 * * values (optional): string array, varies by milestone
 *
 * Namespacing
 * * Entry - page view of a given route
 * * PPA - print, project, or assign actions for a given article
 * * By_____ - indicates what data is stored in `values`
 *
 * @property {'findContentEntry'} findContentEntry
 * *  n: '/find-content' page view
 * @property {'teacherGuideEntry'} teacherGuideEntry
 * * n: '/teacher-guide' page view
 * @property {'assignmentListEntry'} assignmentListEntry
 * * n: '/assignments' page view
 * @property {'productPreviewByType'} productPreviewByType
 * * n: '/article' page loads,
 * * values: product type
 * @property {'ellFilter'} ellFilter
 * * n: findArticle filters of either PETs or AAD Collections for ELLs'
 * @property {'passagePPAByGrade'} passagePPAByGrade
 * * n: reading passage PPA
 * * values: product.canonicalGrade
 * @property {'aadPPA'} aadPPA
 * * n: Article-A-Day PPA
 */

const { journeysMap, JOURNEYS, milestonesMap, MILESTONES, milestonePathsMap } = Object.entries(journeySchema).reduce(
	(acc, [journeyKey, journey]) => {
		acc.journeysMap.set(journeyKey, journey);
		acc.JOURNEYS[journeyKey] = journeyKey;

		const milestones = journey.prerequisites?.milestones;

		if (milestones) {
			for (const milestone in milestones) {
				const milestoneRequirements = milestones[milestone];
				acc.milestonesMap.set(milestone, {
					...(acc.milestonesMap.get(milestone) ?? {}),
					[journeyKey]: milestoneRequirements
				});
				acc.MILESTONES[milestone] = milestone;

				if (milestoneRequirements.path) {
					acc.milestonePathsMap.set(milestoneRequirements.path, new Set([...(acc.milestonePathsMap.get(milestoneRequirements.path) ?? []), milestone]));
				}
			}
		}

		return acc;
	},
	{
		journeysMap: new Map(),
		JOURNEYS: {},
		milestonesMap: new Map(),
		/** @type {MILESTONES} */
		MILESTONES: {},
		milestonePathsMap: new Map()
	}
);

let initialized = false;
const activeJourneys = new Map();
const availableJourneys = new Set();
const blockedJourneys = new Map();
const completedJourneys = new Set();
const prerequisites = new Map(); // triggerRef, journeyKey(string)[], triggerRef => `journey-name-step-4` || milestoneKey
let modalOpen = false;
let isExternal = false;
const clickWatchers = new Set();

const needsReset = timestamp => {
	const now = new Date();
	const month = now.getMonth();
	const year = now.getFullYear();
	let resetYear = month < 6 ? year - 1 : year;

	const resetDate = new Date(resetYear, 6).getTime() / 1000;

	return timestamp < resetDate;
};

const checkMilestone = milestone => {
	const milestoneRequirements = milestonesMap.get(milestone);
	const { n = 0, values = [], u } = model.get('milestoneData')?.[milestone] ?? {};
	const journeyNeedsProgress = new Set();
	const outdated = needsReset(u);

	for (const journeyKey in milestoneRequirements) {
		const { target, values: requiredValues } = milestoneRequirements[journeyKey];

		if (outdated || n < target) {
			journeyNeedsProgress.add(journeyKey);
		} else if (requiredValues) {
			if (Array.isArray(requiredValues)) {
				const matchingValues = values.filter(value => requiredValues.includes(value));

				if (matchingValues.length < target) {
					journeyNeedsProgress.add(journeyKey);
				}
			} else if (requiredValues === 'distinct') {
				const distinctValues = new Set(values);

				if (distinctValues.size < target) {
					journeyNeedsProgress.add(journeyKey);
				}
			}
		}
	}

	return {
		needsProgress: journeyNeedsProgress.size > 0,
		journeyNeedsProgress,
		outdated
	};
};

const getPrerequisiteKey = (name, step) => {
	let key = name.replace(/[_ .]/g, '-').toLowerCase();

	if (step) {
		key = `${key}-step-${step}`;
	}

	return key;
};

const removePrerequisite = prerequisiteKey => {
	if (prerequisites.has(prerequisiteKey)) {
		const unblockedJourneys = prerequisites.get(prerequisiteKey);
		unblockedJourneys.forEach(journeyKey => {
			const { remainingPrerequisites = new Set() } = blockedJourneys.get(journeyKey) ?? {};
			remainingPrerequisites.delete(prerequisiteKey);

			if (remainingPrerequisites.size === 0) {
				blockedJourneys.delete(journeyKey);
				availableJourneys.add(journeyKey);
				// eslint-disable-next-line no-use-before-define
				startJourney(journeyKey);
			}
		});
	}
};

const addPrerequisite = (prerequisiteKey, journeyKey) => {
	if (prerequisites.has(prerequisiteKey)) {
		const matchingStep = prerequisites.get(prerequisiteKey);
		matchingStep.push(journeyKey);
		prerequisites.set(prerequisiteKey, matchingStep);
	} else {
		prerequisites.set(prerequisiteKey, [journeyKey]);
	}
};

const initPrerequisites = sameSession => {
	journeysMap.forEach((journey, journeyKey) => {
		const { prerequisites: prereqs } = journey;

		const isActive = activeJourneys.has(journeyKey);
		const isCompleted = completedJourneys.has(journeyKey);
		if (!(isActive || isCompleted)) {
			let shouldStartJourney = false;

			if (prereqs && !isExternal) {
				const { journeys: preJourneys = [], milestones = {} } = prereqs;
				const remainingPrerequisites = new Set();
				preJourneys.forEach(({ name, step }) => {
					const lastStep = journeysMap.get(name).steps.length;
					const stepToCompare = typeof step === 'number' && step <= lastStep ? step : lastStep;
					const activeJourney = activeJourneys.get(journeyKey);

					if (!activeJourney || activeJourney.idx + 1 < stepToCompare) {
						const prerequisiteKey = getPrerequisiteKey(name, stepToCompare);
						addPrerequisite(prerequisiteKey, journeyKey);
						remainingPrerequisites.add(prerequisiteKey);
					}
				});

				for (const milestone in milestones) {
					const { journeyNeedsProgress } = checkMilestone(milestone);

					if (journeyNeedsProgress.has(journeyKey)) {
						const prerequisiteKey = getPrerequisiteKey(milestone, journeyKey);
						addPrerequisite(prerequisiteKey, journeyKey);
						remainingPrerequisites.add(prerequisiteKey);
					} else if (milestones[milestone].nextSession && !sameSession) {
						// milestone cleared in previous session
						shouldStartJourney = true;
					}
				}

				if (remainingPrerequisites.size > 0) {
					blockedJourneys.set(journeyKey, {
						remainingPrerequisites
					});
				}
			}

			if (!blockedJourneys.has(journeyKey)) {
				availableJourneys.add(journeyKey);

				if (shouldStartJourney) {
					// eslint-disable-next-line no-use-before-define
					startJourney(journeyKey);
				}
			}
		}
	});
};

/**
 *
 * @param {keyof MILESTONES} milestone
 * @param {string} value
 * @returns {Promise<void>}
 */
const updateMilestone = async (milestone, value) => {
	if (!userContext.loggedIn() || isExternal) return;

	const { needsProgress, journeyNeedsProgress, outdated: reset } = checkMilestone(milestone);

	if (needsProgress) {
		const { n, values, u } = await accountService.updateMilestone({
			milestone,
			value: typeof value === 'string' ? value : JSON.stringify(value),
			reset
		});

		const milestoneData = model.get('milestoneData') ?? {};
		const currentData = milestoneData[milestone];

		model.set('milestoneData', {
			...milestoneData,
			[milestone]: {
				...currentData,
				n,
				values,
				u
			}
		});

		const { journeyNeedsProgress: updatedJourneyData } = checkMilestone(milestone);

		if (updatedJourneyData.size < journeyNeedsProgress.size) {
			for (const journeyKey of journeyNeedsProgress) {
				const requirements = milestonesMap.get(milestone)[journeyKey];

				if (!updatedJourneyData.has(journeyKey) && !requirements.nextSession) {
					removePrerequisite(getPrerequisiteKey(milestone, journeyKey));
				}
			}
		}
	}
};

const updatePPAMilestone = async product => {
	const ppaMilestoneDict = {
		A: {
			milestone: 'passagePPAByGrade',
			value: product.canonicalGrade
		},
		AAD: {
			milestone: 'aadPPA'
		}
	};

	const milestoneInfo = ppaMilestoneDict[product.productType] ?? {};
	const needsValue = 'value' in milestoneInfo;
	const { milestone, value } = milestoneInfo;

	if (milestone && MILESTONES[milestone] && !(needsValue && !value)) {
		await updateMilestone(milestone, value);
	}
};

const navigateToStep = currentStep => {
	const { path: basePath, pageParams } = currentStep;

	if (basePath && !window.location.href.includes('test.html') && window.location.pathname.indexOf(basePath[0]) !== 0) {
		let path = basePath[0];
		if (basePath.length === 2) {
			path += basePath[1];
		}

		if (Array.isArray(pageParams)) {
			path = pageParams.reduce((acc, param) => {
				return `${acc}${param}/`;
			}, `${path}#!`);
		}

		page(path);
	}
};

const showStep = (el, options) => {
	let step;
	if (el || options) {
		step = Object.assign(
			{},
			{
				el
			},
			options
		);
	}
	pubsub.publish('journey-dialog-update', step);
};

const updateActiveJourney = (journeyKey, navigate = false) => {
	if (journeysMap.has(journeyKey) && activeJourneys.has(journeyKey)) {
		const journey = journeysMap.get(journeyKey);
		const journeyData = activeJourneys.get(journeyKey);
		const { icon, title: journeyTitle } = journey;

		const currentStep = journey.steps[journeyData.idx];
		const {
			nodes,
			trigger = {
				type: 'presence'
			},
			title,
			text,
			links,
			hidePrev,
			hideNext
		} = currentStep;

		const query = nodes?.map(({ selector }) => selector);

		if (navigate) {
			navigateToStep(currentStep);
		}

		const setWatcher = (eventData = {}) => {
			let watcher = watchers.get(journeyKey);
			if (watcher && watcher.name === journeyKey) {
				watcher.stop();
			}

			watcher = new DOMWatcher(
				query,
				results => {
					if (modalOpen) {
						showStep();
						return;
					}

					let highlightedElement = null;
					let dialogPosition = 'b';
					const { coordinates } = eventData;
					if (results && results.length) {
						const visibleResults = results.filter(isVisible);

						if (visibleResults.length > 0) {
							highlightedElement = visibleResults[0];
							const { position, skipsProgress } = nodes.find(({ selector, silent }) => {
								return highlightedElement.matches(selector) && !silent;
							});
							if (position) {
								dialogPosition = position;
							}

							visibleResults.forEach(result => {
								if (!clickWatchers.has(result) && !skipsProgress) {
									const next = () => {
										result.removeEventListener('mousedown', next);
										clickWatchers.delete(result);
										showStep(); // Clear dialog on mousedown
										nextStep(journeyKey); //eslint-disable-line no-use-before-define
									};
									result.addEventListener('mousedown', next);
									clickWatchers.add(result);
								}
							});
						}
					}
					if (highlightedElement) {
						showStep(highlightedElement, {
							journeyKey,
							journeyTitle,
							coordinates,
							position: dialogPosition,
							icon,
							title,
							text,
							links,
							hidePrev,
							hideNext,
							startTimestamp: journeyData.s,
							currentStep: journeyData.idx + 1,
							totalSteps: journey.steps.length
						});
					} else {
						showStep(highlightedElement, {
							journeyKey,
							currentStep: journeyData.idx + 1
						});
					}
				},
				{
					name: journeyKey,
					targetEl: document.querySelector('.page'),
					domBatchTime: 200
				}
			);

			watchers.set(journeyKey, watcher);
			watcher.start();
		};
		if (trigger.type === 'presence') {
			setWatcher();
		} else if (trigger.type === 'event') {
			const eventSub = pubsub.subscribe('analytics-event', eventData => {
				if (eventData.name === trigger.event) {
					setWatcher(eventData);
					eventSub.unsubscribe();
				}
			});
		}

		removePrerequisite(getPrerequisiteKey(journeyKey, journeyData.idx));
	}
};

const updateActiveJourneys = userJourneys => {
	if (userJourneys.size > 0) {
		for (const [journeyKey, journeyData] of userJourneys) {
			const journeyExists = journeysMap.has(journeyKey);
			if (journeyExists) {
				if (needsReset(journeyData.s)) {
					continue;
				}

				if (journeyData.c) {
					completedJourneys.add(journeyKey);
				} else {
					activeJourneys.set(journeyKey, journeyData);
					updateActiveJourney(journeyKey);
				}
			}
		}
	}
};

const updateJourney = (journeyKey, idx, c, navigate = false) => {
	let updatedData = {
		j: journeyKey
	};
	let existingJourneyData = activeJourneys.get(journeyKey);
	let { s } = existingJourneyData || {};

	if (c) {
		updatedData.c = c;
	} else {
		updatedData.idx = idx;

		if (!s || idx === 0) {
			s = new Date().getTime() / 1000;
		}

		existingJourneyData = {
			idx,
			s
		};
		activeJourneys.set(journeyKey, Object.assign({}, existingJourneyData));
		updateActiveJourney(journeyKey, navigate);
	}

	queue.push(
		() => {
			if (isExternal) {
				return new Promise(resolve => {
					const currentStep = journeysMap.get(journeyKey).steps[idx];
					if (currentStep && currentStep.skipIfExternal) {
						// eslint-disable-next-line no-use-before-define
						incrementStep(journeyKey, 'next', true);
					}

					pubsub.publish('journeys-update');
					resolve();
				});
			}

			return accountService.updateJourney(updatedData).then(({ updated }) => {
				const { s: timestamp } = updated.j[journeyKey];

				if (!c && timestamp && timestamp !== s) {
					activeJourneys.set(
						journeyKey,
						Object.assign({}, existingJourneyData, {
							s: timestamp
						})
					);
				}

				pubsub.publish('journeys-update');
			});
		},
		err => logger.error(err)
	);
};

const enableJourney = journeyKey => {
	const dismissedJourneys = model.get('dismissedJourneys') || {};
	if (dismissedJourneys[journeyKey]) {
		dismissedJourneys[journeyKey] = false;
		model.set('dismissedJourneys', dismissedJourneys);
	}
};

const dismissJourney = journeyKey => {
	const dismissedJourneys = model.get('dismissedJourneys') || {};
	if (!dismissedJourneys[journeyKey]) {
		dismissedJourneys[journeyKey] = true;
		model.set('dismissedJourneys', dismissedJourneys);
	}
};

const startJourney = (journeyKey, forceReset = false) => {
	const available = availableJourneys.has(journeyKey);

	if ((available && !isExternal) || forceReset) {
		availableJourneys.delete(journeyKey);
		completedJourneys.delete(journeyKey);
		enableJourney(journeyKey);
		updateJourney(journeyKey, 0, false, forceReset === true);
	}
};

const incrementStep = (journeyKey, direction = 'next', navigate = false) => {
	if (activeJourneys.has(journeyKey)) {
		const activeJourney = activeJourneys.get(journeyKey);

		const { idx } = activeJourney;

		const { steps, onComplete } = journeysMap.get(journeyKey);

		if (direction === 'next' && idx >= steps.length - 1) {
			const watcher = watchers.get(journeyKey);
			if (watcher && watcher.name === journeyKey) {
				watcher.stop();
				watchers.delete(journeyKey);
			}
			activeJourneys.delete(journeyKey);
			completedJourneys.add(journeyKey);
			updateJourney(journeyKey, null, true, navigate);
			showStep();

			if (onComplete && Array.isArray(onComplete.startJourneys)) {
				onComplete.startJourneys.forEach(key => {
					startJourney(key);
				});
			}
		} else {
			let targetIndex = idx;
			if (direction === 'next') {
				targetIndex++;
			} else if (idx > 0) {
				targetIndex--;
			}

			updateJourney(journeyKey, targetIndex, false, navigate);
		}
	}
};

const nextStep = (journeyKey, navigate = false) => {
	incrementStep(journeyKey, 'next', navigate);
};

const prevStep = (journeyKey, navigate = false) => {
	incrementStep(journeyKey, 'prev', navigate);
};

const triggerWatchers = () => {
	watchers.forEach(watcher => {
		watcher.processNodes();
	});
};

const handleModal = open => {
	triggerWatchers();
	modalOpen = open;
};

const mapByKey = (arr = [], key = '') => {
	return (
		arr.reduce((acc, obj) => {
			return {
				...acc,
				[obj[key] ?? key]: obj
			};
		}, {}) ?? {}
	);
};

const initJourneys = async (forceReset = false) => {
	if (model.get('gcAddon')) {
		return;
	}

	if (!initialized || forceReset) {
		initialized = true;
		isExternal = userContext.isExternal();

		pubsub.subscribe('modal-opened', () => {
			handleModal(true);
		});

		pubsub.subscribe('modal-closed', () => {
			handleModal(false);
		});

		pubsub.subscribe('mobile-menu-toggled', () => {
			triggerWatchers();
		});

		pubsub.subscribe('route-changing', ({ p: currentPath }) => {
			if (milestonePathsMap.has(currentPath)) {
				for (const milestone of milestonePathsMap.get(currentPath)) {
					updateMilestone(milestone);
				}
			}
			showStep();
		});

		const userData = await accountService.getJourneys();

		if (userData) {
			const userJourneys = new Map(Object.entries(userData));
			updateActiveJourneys(userJourneys);
		}

		let hasMilestoneData = !!model.get('milestoneData');

		if (!hasMilestoneData || forceReset) {
			const milestones = await accountService.getMilestones();
			const milestoneData = mapByKey(milestones, 'milestone');
			model.set('milestoneData', milestoneData);
		}
		initPrerequisites(hasMilestoneData);

		pubsub.publish('journeys-update');
	}
};

module.exports = {
	JOURNEYS,
	startJourney,
	initJourneys,
	nextStep,
	prevStep,
	journeysMap,
	activeJourneys,
	availableJourneys,
	blockedJourneys,
	completedJourneys,
	enableJourney,
	dismissJourney,
	watchers,
	navigateToStep,
	MILESTONES,
	updateMilestone,
	updatePPAMilestone
};
